import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  InputGroup,
  Text,
} from '@chakra-ui/react';
import useApi from 'hooks/useApi';
import Pagination from 'components/pagination/Pagination';
import ErrorDisplay from 'components/errorDisplay/ErrorDisplay';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { OverlayLoaderComponent } from 'components/loader/Loader';
import { agentsColumn } from 'views/admin/agents/variables';
import { AGENTS_LISTING_API } from 'constants/endpoints';
import { formatAgentsTableData } from 'utils/common';
import ManageColumns from 'components/manageColumns/ManageColumns';
import NoDataFound from 'components/noDataFound/NoDataFound';
import { FiChevronsDown, FiFilter } from 'react-icons/fi';
import SidebarFilter from 'components/sidebarFilter/SidebarFilter';
import CustomTable from 'components/table/CustomTable';
import { getFilterFromLocalStorage } from 'utils/common';
import { ENTITY_FILTER_TYPES } from 'utils/common';
import { formatDateToDDMMYYYY } from 'utils/common';
import { USER_STATUS } from 'constants/filterDropdownData';
import { setFilterToLocalStorage } from 'utils/common';
import Menu from 'components/menu/MainMenu';
import { PROPERTY_STATUS } from 'constants/filterDropdownData';
import { UPDATE_USER_AND_PROPERTY_API } from 'constants/endpoints';
import { showToast } from 'components/toast/Toast';
import { areAllStatusesSame } from 'utils/common';
import MultiSelectMenu from 'components/menu/MultiSelectMenu';
import { useHistory } from 'react-router-dom';
import { COLLECTION_TYPE } from 'constants/defaultState';

const PAGE_SIZE = 50;

export default function Agents() {
  const { apiCall, error, loading } = useApi();

  const [agentData, setAgentData] = useState([]);

  const [searchText, setSearchText] = useState('');

  const [selectedAction, setSelectedAction] = useState('');

  const lsFiltersAgentsPageSize = getFilterFromLocalStorage(
    ENTITY_FILTER_TYPES.AGENTS_PAGE_SIZE
  );

  const [pageSize, setPageSize] = useState(
    lsFiltersAgentsPageSize ? lsFiltersAgentsPageSize : PAGE_SIZE
  );

  const lsAdsDateRange = getFilterFromLocalStorage(
    ENTITY_FILTER_TYPES.AGENTS_DATE_RANGE
  );

  const [startDate, setStartDate] = useState(
    lsAdsDateRange ? lsAdsDateRange[0] : ''
  );
  const [endDate, setEndDate] = useState(
    lsAdsDateRange ? lsAdsDateRange[1] : ''
  );

  const lsFilters = getFilterFromLocalStorage(
    ENTITY_FILTER_TYPES.AGENTS_FILTER
  );

  const [filters, setFilters] = useState(
    lsFilters
      ? lsFilters
      : {
          status: '',
        }
  );

  const { status = ''} = filters || {};
  const [isOpenFilterSidebar, setIsOpenFilterSidebar] = useState(false);

  const [filteredData, setFilteredData] = useState(
    lsFilters ? Object.keys(lsFilters).filter((item) => filters[item]) : []
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(10);
  const [filterRemoveFlagCount, setFilterRemoveFlagCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedRowsData, setSelectedRowsData] = useState([]);

  const [manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox, setManageColumnSelectAllCheckbox] =
    useState(false);
  const history = useHistory();

  const fetchData = useCallback(async () => {
    try {
      const data = await apiCall.get(`${AGENTS_LISTING_API}`, {
        status,
        search_text: searchText,
        page: currentPage,
        limit: pageSize,
        start_date: formatDateToDDMMYYYY(startDate),
        end_date: formatDateToDDMMYYYY(endDate),
      });

      const formatData = data?.data?.data;

      setTotalPages(Math.ceil(data?.data?.count / pageSize));
      setTotalCount(data?.data?.count);
      setAgentData(formatData);
    } catch (error) {
      console.log(error);
    }
  }, [status, startDate, endDate, searchText, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [filterRemoveFlagCount, currentPage, pageSize]);

  const columns = useMemo(() => agentsColumn, [agentsColumn]);
  const data = useMemo(() => formatAgentsTableData(agentData), [agentData]);

  const [columnVisibility, setColumnVisibility] = useState(
    columns.reduce((acc, column) => {
      acc[column.accessor] = true;
      return acc;
    }, {})
  );

  const handleColumnChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  const visibleColumns = columns.filter(
    (column) => columnVisibility[column.accessor]
  );

  const toggleSidebar = () => {
    setIsOpenFilterSidebar(!isOpenFilterSidebar);
  };
  const handleInputFormSubmit = (e) => {
    e.preventDefault();

    setCurrentPage(1);
    fetchData();
  };

  const handleApplyFilter = () => {
    const applyFilterArray = filters
      ? Object.keys(filters).filter((key) => filters[key])
      : [];

    setFilteredData(applyFilterArray);
    setFilterToLocalStorage(ENTITY_FILTER_TYPES.AGENTS_FILTER, filters);
    setCurrentPage(1);

    setSelectedRowsData([]);
    fetchData();
  };

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    setCurrentPage(1);
  }, []);

  const handleRemoveAllFilter = () => {
    setFilteredData([]);

    setFilters({
      status: '',
    });

    setEndDate('');
    setStartDate('');
  };

  const handleRowSelect = useCallback(
    (row) => {
      const findData = selectedRowsData.find(
        (item) => item.slug_url === row.slug_url
      );

      setSelectedRowsData((prev) => {
        if (findData) {
          return prev.filter((item) => item.slug_url !== findData.slug_url);
        } else {
          return [...prev, row];
        }
      });
    },
    [setSelectedRowsData, selectedRowsData]
  );

  const selectedRowsSlug = selectedRowsData.map((item) => item.slug_url);

  const selectedData = useMemo(
    () =>
      selectedRowsSlug.map((slug) => data.find((row) => row.slug_url === slug)),
    [selectedRowsSlug, data]
  );

  const dropdownFilters = [
    {
      name: 'status',
      placeholder: 'Select Status',
      value: filters?.status,
      data: PROPERTY_STATUS,
    },
  ];

  const allFiltersProps = {
    dropdowns: dropdownFilters,
  };

  const filterBySlugUrl = (smallArray, fullArray) => {
    return fullArray.filter((fullItem) =>
      smallArray.some((smallItem) => smallItem.slug_url === fullItem.slug_url)
    );
  };

  const handleMultipleUpdateStatus = async ({ updatedStatus }) => {
    const statusArray = selectedRowsData?.map((item) => item?.status);

    if (!areAllStatusesSame(statusArray)) {
      showToast({
        message: 'All selected properties must have the same status',
        customStyle: {
          background: '#ff4d4f',
          color: '#fff',
          padding: '10px',
        },
      });
      return;
    }

    const filterdToBeUpdatedData = filterBySlugUrl(selectedRowsData, agentData);

    const updatedData = filterdToBeUpdatedData.map((item) => {
      return {
        type: 'agents',
        _id: item._id,

        status: updatedStatus,
      };
    });

    try {
      await apiCall.patch(UPDATE_USER_AND_PROPERTY_API, {
        updateData: updatedData,
      });

      setFilterRemoveFlagCount((prev) => prev + 1);

      showToast({
        message: 'Property status updated successfully',
        customStyle: {
          background: 'green',
          color: '#fff',
          padding: '10px',
        },
      });

      setSelectedRowsData([]);
    } catch (error) {}
  };

  const handleUpdatePropertyStatus = async ({
    updatedPropertyType,
    slug,
    id: selectedId
  }) => {
    try {
      const data = agentData.find((item) => item._id === selectedId);
      const {_id, company, user} = data || {};
      if (updatedPropertyType === "/admin/form") {
        const formId = company?._id || _id;
        const formType = company ? COLLECTION_TYPE.Company : COLLECTION_TYPE.Agent;
        const companyName = company?.name || '';
        
        history.push(
          `/admin/builder?id=${formId}&type=${formType}&agent_id=${_id}&company_name=${companyName}&slug_url=${user?.slug_url}`
        );
        return;
      }
      const { _id: id } = data;
      await apiCall.patch(UPDATE_USER_AND_PROPERTY_API, {
        updateData: [
          {
            type: 'agents',

            _id: id,

            status: updatedPropertyType,
          },
        ],
      });

      setFilterRemoveFlagCount((prev) => prev + 1);

      showToast({
        message: 'Property status updated successfully',
        customStyle: {
          background: 'green',
          color: '#fff',
          padding: '10px',
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <Box height="100vh" bg="#F8FAFB">
      <Flex
        justify="space-between"
        align="center"
        p="2"
        // bg={useColorModeValue("gray.50", "gray.700")}
        boxShadow="sm"
        borderRadius="sm"
        pos="fixed"
        style={{ width: 'calc(100vw - 96px)' }}
        zIndex={1000}
      >
        <HStack spacing="4" p={2}>
          <Box position="relative" display="inline-block">
            <Button
              paddingX={6}
              leftIcon={<FiFilter />}
              rightIcon={<FiChevronsDown />} // Changed the icon to ChevronDown
              colorScheme="teal"
              variant="outline"
              borderRadius="md"
              size="sm"
              onClick={() => {
                toggleSidebar();
                setFilters(lsFilters ? lsFilters : { status: '' });
              }}
            >
              Filter
            </Button>
            {filteredData.length > 0 && (
              <Badge
                position="absolute"
                top="-3"
                right="0"
                colorScheme="teal"
                borderRadius="full"
                px={2}
                fontSize="0.8em"
              >
                {filteredData.length}
              </Badge>
            )}
          </Box>

          <ManageColumns
            columns={columns}
            handleColumnChange={handleColumnChange}
            columnVisibility={columnVisibility}
            setManageColumnSelectAllCheckbox={setManageColumnSelectAllCheckbox}
            manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox={manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox}
          />

          <form onSubmit={handleInputFormSubmit}>
            <InputGroup size="sm">
              <SearchBar
                background="#F9F9FB"
                color="#637381"
                setSearchText={(e) => setSearchText(e.target.value)}
                searchText={searchText}
                borderRadius="5px"
                borderWidth="0.5px"
                borderColor="#637381"
              />
            </InputGroup>
          </form>
        </HStack>
        <Button
          borderRadius="5px"
          borderColor="#637381"
          background={"linear-gradient(180deg, #22ACEE 0%, #0177B4 100%)"}
          color="#ffffff"
          fontSize="sm"
          colorScheme="teal"
          onClick={() => {
            history.push("/admin/builder");
          }}
        >
          Create Agent
        </Button>
        <Text
          borderRadius="5px"
          borderWidth="0.5px"
          borderColor="#637381"
          background="#F9F9FB"
          color="#637381"
          p={1}
        >
          Total Agents: {totalCount}
        </Text>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(value) => setCurrentPage(value)}
          totalCount={totalCount}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setPageSizeToLocalStorage={(value) =>
            setFilterToLocalStorage(ENTITY_FILTER_TYPES.AGENTS_PAGE_SIZE, value)
          }
        />
      </Flex>

      <SidebarFilter
        filters={allFiltersProps}
        handleFilterChange={handleFilterChange}
        handleApplyFilter={handleApplyFilter}
        isOpen={isOpenFilterSidebar}
        onClose={toggleSidebar}
        appliedFilters={filters}
        setFilters={setFilters}
        lsFilters={lsFilters ? lsFilters : { status: '' }}
        handleRemoveAllFilter={handleRemoveAllFilter}
        //calender filter
        isCalenderFilter={true}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />

      {agentData.length !== 0 ? (
        <>
          <CustomTable
            columns={visibleColumns}
            data={data}
            onSelectedAction={setSelectedAction}
            selectedRowsData={selectedRowsData}
            selectedRowsSlug={selectedRowsSlug}
            selectedData={selectedData}
            handleRowSelect={handleRowSelect}
            setSelectedRowsData={setSelectedRowsData}
            // single menu props
            menuComponent={
              <Menu
                data={Object.values(USER_STATUS)}
                onUpdate={() => {}}
                onUpdatePropertyStatus={handleUpdatePropertyStatus}
                selectedAction={selectedAction}
              />
            }
            //multi select menu props
            multiSelectMenuComponent={
              selectedRowsSlug?.length > 0 &&
              areAllStatusesSame(
                selectedData.map((item) => item?.property_status)
              ) && (
                <MultiSelectMenu
                  multiSelectCurrentValue={selectedData[0]?.property_status}
                  onMultipleUpdateStatus={handleMultipleUpdateStatus}
                  selectedAction={selectedAction}
                  selectedRowsData={selectedRowsData}
                  bgColor="#B2F5EA"
                />
              )
            }
          />
        </>
      ) : !loading ? (
        <NoDataFound />
      ) : null}

      <OverlayLoaderComponent isOpen={loading} />
    </Box>
  );
}
